import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Pagination,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AccessTime from '@mui/icons-material/AccessTime';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import { getProfile } from 'client/modules/Auth/AuthReducer';
import callApi from 'client/util/apiCaller';
import React, { useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PricingPlans from '../../../Public/Pricing/pages/PricingPage/PricingPlans';
import { getPlans } from '../../../Public/Pricing/PricingReducer';
import CancelSubscriptionDialog from './CancelSubscriptionDialog';
import UpdateBillingAddressDialog from './UpdateBillingAddressDialog';
import UpdatePaymentMethodDialog from './UpdatePaymentMethodDialog';
import UpdateTaxInfoDialog from './UpdateTaxInfoDialog';

const messages = defineMessages({
  title: {
    id: 'Billing.Title',
    defaultMessage: 'Billing & Subscription',
  },
  subscription: {
    id: 'Billing.Subscription',
    defaultMessage: 'Subscription Management',
  },
  paymentMethods: {
    id: 'Billing.PaymentMethods',
    defaultMessage: 'Payment Methods',
  },
  billingHistory: {
    id: 'Billing.BillingHistory',
    defaultMessage: 'Billing History',
  },
  manageSubscription: {
    id: 'Billing.ManageSubscription',
    defaultMessage: 'Manage Subscription',
  },
  managePaymentMethods: {
    id: 'Billing.ManagePaymentMethods',
    defaultMessage: 'Manage Payment Methods',
  },
  customerInfo: {
    id: 'Billing.CustomerInfo',
    defaultMessage: 'Customer Information',
  },
  billingAddress: {
    id: 'Billing.BillingAddress',
    defaultMessage: 'Billing Address',
  },
  editPaymentMethod: {
    id: 'Billing.EditPaymentMethod',
    defaultMessage: 'Edit Payment Method',
  },
  editAccountDetails: {
    id: 'Billing.EditAccountDetails',
    defaultMessage: 'Edit account details',
  },
  editBillingAddress: {
    id: 'Billing.EditBillingAddress',
    defaultMessage: 'Edit billing address',
  },
  taxInfo: {
    id: 'Billing.TaxInfo',
    defaultMessage: 'Tax information',
  },
  editTaxInfo: {
    id: 'Billing.EditTaxInfo',
    defaultMessage: 'Edit tax information',
  },
  taxBusinessName: {
    id: 'Billing.TaxBusinessName',
    defaultMessage: 'Legal business name',
  },
  taxId: {
    id: 'Billing.TaxId',
    defaultMessage: 'VAT / tax ID',
  },
  taxIdMissing: {
    id: 'Billing.TaxIdMissing',
    defaultMessage: 'No tax ID on file. Add your VAT number for correct tax treatment on invoices.',
  },
  taxIdVerified: {
    id: 'Billing.TaxIdVerified',
    defaultMessage: 'Verified',
  },
  taxIdPending: {
    id: 'Billing.TaxIdPending',
    defaultMessage: 'Verification pending',
  },
  taxIdUnverified: {
    id: 'Billing.TaxIdUnverified',
    defaultMessage: 'Unverified',
  },
  nextBilling: {
    id: 'Billing.NextBilling',
    defaultMessage: 'Next billing: {date}',
  },
  cardExpires: {
    id: 'Billing.CardExpires',
    defaultMessage: 'Expires {month}/{year}',
  },
  statusActive: {
    id: 'Billing.Status.Active',
    defaultMessage: 'Active',
  },
  statusTrial: {
    id: 'Billing.Status.Trial',
    defaultMessage: 'Trial',
  },
  statusCancelled: {
    id: 'Billing.Status.Cancelled',
    defaultMessage: 'Cancelled',
  },
  statusPastDue: {
    id: 'Billing.Status.PastDue',
    defaultMessage: 'Past Due',
  },
  statusCancelling: {
    id: 'Billing.Status.Cancelling',
    defaultMessage: 'Cancelling',
  },
  statusCancelsOn: {
    id: 'Billing.Status.CancelsOn',
    defaultMessage: 'Cancels {date}',
  },
  activeUntil: {
    id: 'Billing.ActiveUntil',
    defaultMessage: 'Active until {date}',
  },
  serviceEndsOn: {
    id: 'Billing.ServiceEndsOn',
    defaultMessage: 'Your service will end on {date}.',
  },
  manage: {
    id: 'Billing.Manage',
    defaultMessage: 'Manage',
  },
  cancelSubscription: {
    id: 'Billing.CancelSubscription',
    defaultMessage: 'Cancel subscription',
  },
  changePlan: {
    id: 'Billing.ChangePlan.Button',
    defaultMessage: 'Change plan',
  },
  resumeSubscription: {
    id: 'Billing.ResumeSubscription',
    defaultMessage: 'Resume subscription',
  },
  billingHistoryDescription: {
    id: 'Billing.BillingHistoryDescription',
    defaultMessage: 'Download your tax invoices issued for Promoter Ninja payments.',
  },
  invoiceDate: {
    id: 'Billing.InvoiceDate',
    defaultMessage: 'Date',
  },
  invoiceNumber: {
    id: 'Billing.InvoiceNumber',
    defaultMessage: 'Invoice',
  },
  invoiceAmount: {
    id: 'Billing.InvoiceAmount',
    defaultMessage: 'Amount',
  },
  downloadInvoice: {
    id: 'Billing.DownloadInvoice',
    defaultMessage: 'Download',
  },
  noInvoices: {
    id: 'Billing.NoInvoices',
    defaultMessage: 'No invoices yet. Invoices appear here after your first payment.',
  },
  invoiceDownloadError: {
    id: 'Billing.InvoiceDownloadError',
    defaultMessage: 'Could not download the invoice. Please try again.',
  },
  errorLoading: {
    id: 'Billing.Error',
    defaultMessage: 'Error loading billing information. Please try again.',
  },
});

const INVOICES_PER_PAGE = 10;

function BillingPage() {
  const { formatNumber, locale } = useIntl();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const profile = useSelector(getProfile);
  const [subscription, setSubscription] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [paymentSource, setPaymentSource] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoicePagination, setInvoicePagination] = useState({
    total: 0,
    limit: INVOICES_PER_PAGE,
    skip: 0,
    hasMore: false,
  });
  const [invoiceError, setInvoiceError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [taxDialogOpen, setTaxDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const plans = useSelector(getPlans);

  const applySubscriptionResponse = useCallback(ret => {
    setSubscription(ret.subscription);
    setCustomer(ret.customer);
    setPaymentSource(ret.paymentSource);
  }, []);

  const fetchSubscription = useCallback(() => {
    setLoading(true);
    setError(null);
    return callApi(dispatch, 'users/subscriptions', null, 'GET')
      .then(ret => {
        applySubscriptionResponse(ret);
      })
      .catch(err => {
        if (err.status !== 404) {
          setError(err);
        }
        setSubscription(null);
        setCustomer(null);
        setPaymentSource(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [applySubscriptionResponse, dispatch]);

  useEffect(() => {
    let cancelled = false;

    fetchSubscription().then(() => {
      if (cancelled) return;
    });

    return () => {
      cancelled = true;
    };
  }, [fetchSubscription]);

  useEffect(() => {
    const sessionId = searchParams.get('payment_method_session_id');
    if (!sessionId) {
      return;
    }

    let cancelled = false;
    setActionLoading(true);

    callApi(dispatch, 'users/subscriptions/payment-method/confirm-checkout', null, 'POST', {
      sessionId,
    })
      .then(ret => {
        if (cancelled) return;
        applySubscriptionResponse(ret);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('payment_method_session_id');
        setSearchParams(nextParams, { replace: true });
      })
      .catch(() => {
        if (!cancelled) {
          setError({ message: formatMessage(messages.errorLoading) });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setActionLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [applySubscriptionResponse, dispatch, formatMessage, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    const fetchInvoices = () => {
      setInvoicesLoading(true);
      setInvoiceError(null);
      const skip = (invoicePage - 1) * INVOICES_PER_PAGE;
      const params = new URLSearchParams({
        limit: String(INVOICES_PER_PAGE),
        skip: String(skip),
      });
      callApi(dispatch, `users/invoices?${params.toString()}`, null, 'GET')
        .then(ret => {
          if (cancelled) return;
          setInvoices(ret.invoices || []);
          setInvoicePagination(
            ret.pagination || {
              total: 0,
              limit: INVOICES_PER_PAGE,
              skip,
              hasMore: false,
            },
          );
        })
        .catch(err => {
          if (cancelled) return;
          if (err.status !== 404) {
            setInvoiceError(err);
          }
          setInvoices([]);
          setInvoicePagination({
            total: 0,
            limit: INVOICES_PER_PAGE,
            skip: 0,
            hasMore: false,
          });
        })
        .finally(() => {
          if (!cancelled) {
            setInvoicesLoading(false);
          }
        });
    };

    if (!profile.trialEndsAt || subscription) {
      fetchInvoices();
    }

    return () => {
      cancelled = true;
    };
  }, [dispatch, profile.trialEndsAt, subscription, invoicePage]);

  const downloadInvoice = useCallback(
    async invoiceId => {
      try {
        const res = await callApi(dispatch, `users/invoices/${invoiceId}/pdf`, null, 'GET');
        if (res.url) {
          window.open(res.url, '_blank', 'noopener,noreferrer');
        }
      } catch (err) {
        setInvoiceError(err);
      }
    },
    [dispatch],
  );

  const handleCancelSubscription = useCallback(async () => {
    const ret = await callApi(dispatch, 'users/subscriptions/cancel', null, 'POST');
    applySubscriptionResponse(ret);
  }, [applySubscriptionResponse, dispatch]);

  const handleResumeSubscription = useCallback(async () => {
    setActionLoading(true);
    try {
      const ret = await callApi(dispatch, 'users/subscriptions/resume', null, 'POST');
      applySubscriptionResponse(ret);
    } catch (err) {
      setError(err);
    } finally {
      setActionLoading(false);
    }
  }, [applySubscriptionResponse, dispatch]);

  // Show trial message if user is still on trial
  if (profile.trialEndsAt && !subscription) {
    return (
      <Paper style={{ padding: '2em', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Helmet title={formatMessage(messages.title)} />
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            {formatMessage(messages.title)}
          </Typography>
          <Typography variant="body1">
            <FormattedMessage
              id="Billing.TrialActive"
              defaultMessage="Your trial is currently active. Billing information will be available once you upgrade to a paid plan."
            />
          </Typography>
        </Box>
        <Box sx={{ mt: 10 }}>
          <PricingPlans />
        </Box>
      </Paper>
    );
  }

  const getPlanName = (planId, sub) =>
    sub?.plan_name || plans.find(p => p.id === planId)?.name || planId;

  const getEmailLimit = (planId, sub) => {
    if (sub?.people) {
      return sub.people;
    }
    const plan = plans.find(p => p.id === planId || p.cf_id === `copy_of_${planId}`);
    return plan ? plan.cf_people || 'unlimited' : 0;
  };

  const formatDate = timestamp => {
    return new Date(timestamp * 1000).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatShortDate = timestamp => {
    const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp);
    const sameYear = date.getFullYear() === new Date().getFullYear();
    return date.toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
  };

  const formatInvoiceDate = dateValue => {
    if (!dateValue) {
      return '—';
    }
    return new Date(dateValue).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatInvoiceAmount = invoice => {
    if (invoice.total == null) {
      return '—';
    }
    return formatNumber(invoice.total / 100, {
      style: 'currency',
      currency: (invoice.currency || 'eur').toUpperCase(),
    });
  };

  const isCancelling = sub =>
    sub?.status === 'non_renewing' ||
    sub?.cancel_at_period_end ||
    (sub?.cancel_at != null && sub.cancel_at > 0);

  const getCancelEndTimestamp = sub => {
    if (!isCancelling(sub)) {
      return null;
    }
    return sub?.ends_at || sub?.cancel_at || null;
  };

  const renderStatusChip = sub => {
    const cancelling = isCancelling(sub);
    if (cancelling) {
      const endTs = getCancelEndTimestamp(sub);
      return (
        <Chip
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span>
                {endTs
                  ? formatMessage(messages.statusCancelsOn, { date: formatShortDate(endTs) })
                  : formatMessage(messages.statusCancelling)}
              </span>
              <AccessTime sx={{ fontSize: 14 }} />
            </Box>
          }
          size="small"
          sx={{
            bgcolor: 'grey.100',
            color: 'text.secondary',
            fontWeight: 500,
            border: '1px solid',
            borderColor: 'grey.300',
            '& .MuiChip-label': { px: 1 },
          }}
        />
      );
    }

    return (
      <Chip
        label={getStatusLabel(sub.status, false)}
        size="small"
        color={getStatusColor(sub.status, false)}
      />
    );
  };

  const getStatusColor = (status, cancelling) => {
    if (cancelling) {
      return 'warning';
    }
    const colors = {
      active: 'success',
      trial: 'info',
      cancelled: 'error',
      past_due: 'warning',
      non_renewing: 'warning',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status, cancelling) => {
    if (cancelling) {
      return formatMessage(messages.statusCancelling);
    }
    const labels = {
      active: formatMessage(messages.statusActive),
      trial: formatMessage(messages.statusTrial),
      cancelled: formatMessage(messages.statusCancelled),
      past_due: formatMessage(messages.statusPastDue),
      non_renewing: formatMessage(messages.statusCancelling),
    };
    return labels[status] || status;
  };

  return (
    <Paper style={{ padding: '2em' }}>
      <Helmet title={formatMessage(messages.title)} />
      <Typography variant="h5" component="h2" gutterBottom>
        {formatMessage(messages.title)}
      </Typography>

      {error && (
        <Card variant="outlined" sx={{ mb: 3, borderColor: 'error.main' }}>
          <CardContent>
            <Typography variant="body1" color="error">
              {formatMessage(messages.errorLoading)}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Subscription Details */}
        {loading ? (
          <Card variant="outlined">
            <CardContent>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
              >
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width={120} height={32} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={180} height={48} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={250} height={20} />
                </Box>
                <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: 1 }} />
              </Box>
            </CardContent>
          </Card>
        ) : subscription ? (
          <Card variant="outlined">
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 2,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  {isCancelling(subscription) && (
                    <Box sx={{ mb: 1 }}>{renderStatusChip(subscription)}</Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="h6" component="h3">
                      {getPlanName(subscription.plan_id, subscription)}
                    </Typography>
                    {!isCancelling(subscription) && renderStatusChip(subscription)}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                    <Typography variant="h4" component="span" sx={{ fontWeight: 'bold' }}>
                      {formatNumber(subscription.plan_amount / 100, {
                        style: 'currency',
                        currency: subscription.currency_code,
                      })}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {subscription.billing_period_unit === 'month' ? (
                        <FormattedMessage
                          id="PricingPlan.Month"
                          defaultMessage="/ {period, plural, one {month} other {# months}}"
                          values={{
                            period: subscription.billing_period,
                          }}
                        />
                      ) : (
                        <FormattedMessage
                          id="PricingPlan.Year"
                          defaultMessage="/ {period, plural, one {year} other {# years}}"
                          values={{
                            period: subscription.billing_period,
                          }}
                        />
                      )}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    <FormattedMessage
                      id="PricingPlan.Sentence"
                      defaultMessage="Send NPS surveys to {number} people monthly."
                      values={{
                        number: formatNumber(getEmailLimit(subscription.plan_id, subscription)),
                      }}
                    />
                  </Typography>
                  {isCancelling(subscription)
                    ? getCancelEndTimestamp(subscription) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <EventOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            <FormattedMessage
                              {...messages.serviceEndsOn}
                              values={{
                                date: formatDate(getCancelEndTimestamp(subscription)),
                              }}
                            />
                          </Typography>
                        </Box>
                      )
                    : subscription.next_billing_at && (
                        <Typography variant="body2" color="text.secondary">
                          <FormattedMessage
                            {...messages.nextBilling}
                            values={{ date: formatDate(subscription.next_billing_at) }}
                          />
                        </Typography>
                      )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {isCancelling(subscription) ? (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleResumeSubscription}
                      disabled={actionLoading}
                    >
                      {formatMessage(messages.resumeSubscription)}
                    </Button>
                  ) : (
                    <>
                      {!subscription.legacy && (
                        <Button
                          variant="outlined"
                          size="small"
                          component={Link}
                          to="/settings/billing/change-plan"
                        >
                          {formatMessage(messages.changePlan)}
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        onClick={() => setCancelDialogOpen(true)}
                      >
                        {formatMessage(messages.cancelSubscription)}
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ mt: 10 }}>
            <PricingPlans />
          </Box>
        )}

        {/* Billing Address */}
        {loading ? (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {formatMessage(messages.billingAddress)}
              </Typography>
              <Skeleton variant="text" width={200} height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={180} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={150} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={120} height={20} sx={{ mb: 2 }} />
              <Skeleton variant="text" width={140} height={20} />
            </CardContent>
          </Card>
        ) : customer?.billing_address ? (
          <Card
            variant="outlined"
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {formatMessage(messages.billingAddress)}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                {[customer.first_name, customer.last_name].filter(Boolean).join(' ') ||
                  customer.email}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {customer.billing_address.line1}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {customer.billing_address.city}, {customer.billing_address.state}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {customer.billing_address.country}
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="text"
                size="small"
                sx={{ m: 1 }}
                onClick={() => setAddressDialogOpen(true)}
              >
                {formatMessage(messages.editBillingAddress)}
              </Button>
            </CardActions>
          </Card>
        ) : null}

        {/* Tax Information */}
        {subscription &&
          (loading ? (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {formatMessage(messages.taxInfo)}
                </Typography>
                <Skeleton variant="text" width={220} height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width={180} height={20} sx={{ mb: 2 }} />
                <Skeleton variant="text" width={140} height={20} />
              </CardContent>
            </Card>
          ) : (
            <Card
              variant="outlined"
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {formatMessage(messages.taxInfo)}
                </Typography>
                {customer?.tax_id?.value ? (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {formatMessage(messages.taxBusinessName)}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                      {customer.business_name ||
                        [customer.first_name, customer.last_name].filter(Boolean).join(' ') ||
                        customer.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {formatMessage(messages.taxId)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                      <Typography
                        component="span"
                        variant="body1"
                        sx={{ fontWeight: 500, lineHeight: 1.5 }}
                      >
                        {customer.tax_id.value}
                      </Typography>
                      {customer.tax_id.verification_status === 'verified' && (
                        <Tooltip title={formatMessage(messages.taxIdVerified)}>
                          <CheckCircleOutline
                            aria-label={formatMessage(messages.taxIdVerified)}
                            sx={{
                              color: 'success.main',
                              fontSize: 20,
                              flexShrink: 0,
                              display: 'block',
                              transform: 'translateY(-1px)',
                            }}
                          />
                        </Tooltip>
                      )}
                      {customer.tax_id.verification_status === 'pending' && (
                        <Chip
                          size="small"
                          color="warning"
                          label={formatMessage(messages.taxIdPending)}
                        />
                      )}
                      {customer.tax_id.verification_status === 'unverified' && (
                        <Chip
                          size="small"
                          color="default"
                          label={formatMessage(messages.taxIdUnverified)}
                        />
                      )}
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {formatMessage(messages.taxIdMissing)}
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <Button
                  variant="text"
                  size="small"
                  sx={{ m: 1 }}
                  onClick={() => setTaxDialogOpen(true)}
                >
                  {formatMessage(messages.editTaxInfo)}
                </Button>
              </CardActions>
            </Card>
          ))}

        {/* Payment Methods */}
        {loading ? (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {formatMessage(messages.paymentMethods)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Skeleton variant="rectangular" width={50} height={30} sx={{ borderRadius: 1 }} />
                <Skeleton variant="text" width={150} height={24} />
              </Box>
              <Skeleton variant="text" width={180} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={120} height={20} sx={{ mb: 2 }} />
              <Skeleton variant="text" width={140} height={20} />
            </CardContent>
          </Card>
        ) : paymentSource?.card ? (
          <Card
            variant="outlined"
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {formatMessage(messages.paymentMethods)}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 30,
                    backgroundColor: '#1a365d',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'white', fontWeight: 'bold', fontSize: '10px' }}
                  >
                    {paymentSource.card.brand.toUpperCase()}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    •••• •••• •••• {paymentSource.card.last4}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                      {...messages.cardExpires}
                      values={{
                        month: paymentSource.card.expiry_month,
                        year: paymentSource.card.expiry_year,
                      }}
                    />
                  </Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {paymentSource.card.first_name} {paymentSource.card.last_name}
                <br />
                {paymentSource.card.billing_country}
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="text"
                size="small"
                sx={{ m: 1 }}
                onClick={() => setPaymentDialogOpen(true)}
              >
                {formatMessage(messages.editPaymentMethod)}
              </Button>
            </CardActions>
          </Card>
        ) : null}

        {/* Billing History */}
        {invoicesLoading ? (
          <Card variant="outlined">
            <CardContent>
              <Skeleton variant="text" width={150} height={32} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={300} height={20} sx={{ mb: 2 }} />
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
            </CardContent>
          </Card>
        ) : (
          subscription && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                  {formatMessage(messages.billingHistory)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {formatMessage(messages.billingHistoryDescription)}
                </Typography>
                {invoiceError && (
                  <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    {formatMessage(messages.invoiceDownloadError)}
                  </Typography>
                )}
                {invoicePagination.total === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {formatMessage(messages.noInvoices)}
                  </Typography>
                ) : (
                  <>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>{formatMessage(messages.invoiceDate)}</TableCell>
                            <TableCell>{formatMessage(messages.invoiceNumber)}</TableCell>
                            <TableCell align="right">
                              {formatMessage(messages.invoiceAmount)}
                            </TableCell>
                            <TableCell align="right" />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {invoices.map(invoice => (
                            <TableRow key={invoice.id}>
                              <TableCell>{formatInvoiceDate(invoice.date)}</TableCell>
                              <TableCell>{invoice.number}</TableCell>
                              <TableCell align="right">{formatInvoiceAmount(invoice)}</TableCell>
                              <TableCell align="right">
                                {invoice.downloadable && (
                                  <Button
                                    variant="text"
                                    size="small"
                                    startIcon={<DownloadOutlined />}
                                    onClick={() => downloadInvoice(invoice.id)}
                                  >
                                    {formatMessage(messages.downloadInvoice)}
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {invoicePagination.total > INVOICES_PER_PAGE && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                          page={invoicePage}
                          count={Math.ceil(invoicePagination.total / INVOICES_PER_PAGE)}
                          onChange={(event, page) => setInvoicePage(page)}
                          size="small"
                          color="primary"
                        />
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )
        )}
      </Box>

      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelSubscription}
        endDate={
          subscription?.next_billing_at
            ? formatDate(subscription.next_billing_at)
            : subscription?.current_term_end
              ? formatDate(subscription.current_term_end)
              : null
        }
      />
      <UpdateBillingAddressDialog
        open={addressDialogOpen}
        onClose={() => setAddressDialogOpen(false)}
        customer={customer}
        onUpdated={applySubscriptionResponse}
      />
      <UpdateTaxInfoDialog
        open={taxDialogOpen}
        onClose={() => setTaxDialogOpen(false)}
        customer={customer}
        onUpdated={applySubscriptionResponse}
      />
      <UpdatePaymentMethodDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        onUpdated={applySubscriptionResponse}
      />
    </Paper>
  );
}

export default BillingPage;
